// Barra de segmentación por SCI (mock, tab Políticas Estándar): edita los 3 cortes atómicamente
// entre los planes del segmento Estándar. Handles arrastrables + inputs numéricos.
// Restricciones: 0 ≤ inicio < bp < po ≤ 100.
import { useRef, useState } from 'react';
import { Button, NumberInput } from '@/kit';
import { cn } from '@/lib/cn';
import { DiscardX } from './ui';

export interface SciCuts {
    /** Dónde arranca Bronce (0 = todos califican). */
    start: number;
    /** Corte Bronce → Plata. */
    bp: number;
    /** Corte Plata → Oro. */
    po: number;
}

export function clampCut(cuts: SciCuts, key: keyof SciCuts, raw: number): number {
    const v = Math.max(0, Math.min(100, Math.round(raw)));
    if (key === 'start') return Math.min(v, cuts.bp - 1);
    if (key === 'bp') return Math.max(cuts.start + 1, Math.min(v, cuts.po - 1));
    return Math.max(cuts.bp + 1, v);
}

const SEGMENTS: {
    key: string;
    label: string;
    className: string;
    range: (c: SciCuts) => { left: number; width: number };
}[] = [
    {
        key: 'nc',
        label: 'No califica',
        className: 'rounded-l-[5px] bg-muted-foreground/15 italic text-muted-foreground',
        range: (c) => ({ left: 0, width: c.start }),
    },
    {
        key: 'bronce',
        label: 'Bronce',
        className: 'bg-[hsl(30_60%_82%)] text-[hsl(24_60%_30%)]',
        range: (c) => ({ left: c.start, width: c.bp - c.start }),
    },
    {
        key: 'plata',
        label: 'Plata',
        className: 'bg-[hsl(220_15%_82%)] text-[hsl(220_15%_30%)]',
        range: (c) => ({ left: c.bp, width: c.po - c.bp }),
    },
    {
        key: 'oro',
        label: 'Oro',
        className: 'rounded-r-[5px] bg-[hsl(45_80%_80%)] text-[hsl(38_60%_25%)]',
        range: (c) => ({ left: c.po, width: 100 - c.po }),
    },
];

const HANDLES: { key: keyof SciCuts; caption: string }[] = [
    { key: 'start', caption: 'Inicio Bronce' },
    { key: 'bp', caption: 'Bronce → Plata' },
    { key: 'po', caption: 'Plata → Oro' },
];

export function SciSegmentBar({
    cuts,
    dirty,
    affectedCount,
    onCutsChange,
    onPublish,
    onDiscard,
}: {
    cuts: SciCuts;
    dirty: boolean;
    affectedCount: number;
    onCutsChange: (cuts: SciCuts) => void;
    onPublish: () => void;
    onDiscard: () => void;
}) {
    const barRef = useRef<HTMLDivElement>(null);
    const [draggingKey, setDraggingKey] = useState<keyof SciCuts | null>(null);

    const setCut = (key: keyof SciCuts, raw: number): void => {
        onCutsChange({ ...cuts, [key]: clampCut(cuts, key, raw) });
    };

    const startDrag = (key: keyof SciCuts) => (e: React.PointerEvent<HTMLDivElement>) => {
        // No iniciar drag desde el input/caption del handle.
        if ((e.target as HTMLElement).closest('[data-handle-label]')) return;
        e.preventDefault();
        const bar = barRef.current;
        if (!bar) return;
        const rect = bar.getBoundingClientRect();
        const startX = e.clientX;
        const startVal = cuts[key];
        const base = { ...cuts };
        setDraggingKey(key);
        const onMove = (ev: PointerEvent): void => {
            const delta = Math.round(((ev.clientX - startX) / rect.width) * 100);
            const next = clampCut(base, key, startVal + delta);
            onCutsChange({ ...base, [key]: next });
        };
        const onUp = (): void => {
            setDraggingKey(null);
            window.removeEventListener('pointermove', onMove);
            window.removeEventListener('pointerup', onUp);
        };
        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);
    };

    // El `pb` reserva el alto de las etiquetas de los handles, que se pintan absolutas fuera de la
    // barra: 6px de offset + input + caption = 40.5px medidos. Con menos se encimaban con la tabla.
    return (
        <div className="relative flex-none border-b border-border bg-card px-4 pb-[46px] pt-2.5">
            <div className="mb-2 flex items-center justify-between text-[10.5px] font-bold uppercase tracking-wide text-muted-foreground">
                <span>Segmentación por SCI · Segmento Estándar</span>
                {dirty ? (
                    <div className="flex items-center gap-1.5 normal-case tracking-normal">
                        <Button size="sm" className="h-6 px-2.5 text-[11px]" onClick={onPublish}>
                            Publicar cambios
                            {affectedCount > 0
                                ? ` (${String(affectedCount)} ${affectedCount === 1 ? 'plan' : 'planes'})`
                                : ''}
                        </Button>
                        <DiscardX onClick={onDiscard} />
                    </div>
                ) : null}
            </div>

            <div ref={barRef} className="relative h-[26px] select-none rounded-[5px] bg-muted">
                {SEGMENTS.map((seg) => {
                    const { left, width } = seg.range(cuts);
                    if (width <= 0) return null;
                    return (
                        <div
                            key={seg.key}
                            className={cn(
                                'pointer-events-none absolute inset-y-0 flex items-center justify-center overflow-hidden whitespace-nowrap text-[10px] font-bold uppercase tracking-wide',
                                seg.className,
                            )}
                            style={{ left: `${String(left)}%`, width: `${String(width)}%` }}
                        >
                            {seg.label}
                        </div>
                    );
                })}

                {HANDLES.map((h) => (
                    <div
                        key={h.key}
                        role="slider"
                        aria-label={h.caption}
                        aria-valuemin={0}
                        aria-valuemax={100}
                        aria-valuenow={cuts[h.key]}
                        onPointerDown={startDrag(h.key)}
                        className={cn(
                            'absolute -bottom-[3px] -top-[3px] z-[2] w-2.5 -translate-x-1/2 cursor-ew-resize rounded border-2 border-card bg-foreground shadow transition-transform',
                            draggingKey === h.key ? 'scale-125' : 'hover:scale-110',
                        )}
                        style={{ left: `${String(cuts[h.key])}%` }}
                    >
                        <div
                            data-handle-label
                            className="absolute left-1/2 top-[calc(100%+6px)] flex min-w-[78px] -translate-x-1/2 cursor-default flex-col items-center gap-px"
                        >
                            <NumberInput
                                value={cuts[h.key]}
                                min={0}
                                max={100}
                                step={1}
                                aria-label={h.caption}
                                className="w-[46px] rounded-[3px] px-1 py-px text-center text-[10.5px] font-bold"
                                onValueChange={(v) => {
                                    setCut(h.key, v);
                                }}
                            />
                            <span className="whitespace-nowrap text-[8.5px] font-bold uppercase tracking-wide text-muted-foreground">
                                {h.caption}
                            </span>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
